import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AppBar,
  Avatar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Tooltip,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Link, useLocation } from 'react-router-dom'
import MenuIcon from '@mui/icons-material/Menu'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import PersonOutline from '@mui/icons-material/PersonOutline'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { getImageDisplayUrl } from '@/services/commonService'
import routerConstants from '@/constants/routerConstants'
import { HE_THONG_PROD_LINKS } from '@/constants/heThongProdLinks'
import { logout } from '@/utils/authUtils'
import { useI18n } from '@/contexts/I18nContext'
import LanguageSwitcher from './LanguageSwitcher'
import TopNavBar, {
  banhangSystemMenu,
  banhangTopMenu,
  buildBanhangMobileMenuSections,
  filterBanhangNavDropdownItems,
  filterBanhangTopMenuByPermissions,
  isBanhangDropdownActive,
  isBanhangPathActive,
} from './TopNavBar'
import HeaderBranchSwitcher from './HeaderBranchSwitcher'
import ThemeAccentPicker from './ThemeAccentPicker'
import { InAppNotificationMenu } from '@/components/notifications'
import { enableVanHanh } from '@/config'
import { LAYOUT_APP_BAR_HEIGHT_PX } from '@/components/layout/layoutConstants'
import {
  banhangKiotLike,
  buildKiotHeaderIconButtonSx,
  buildKiotHeaderNavButtonSx,
  TOP_NAV_HEIGHT_PX,
} from '@/components/layout/banhangKiotTokens'
import { profileStorage } from '@/services/tokenStorage'
import { ALL_CHUCNANG_PERMISSION_CODES } from '@/constants/permissionConstants'
import { useMenuChucNang } from '@/hooks/useMenuChucNang'
import { useCurrentKhoVatLyId } from '@/hooks/useCurrentKhoVatLyId'
import { withCurrentKhoVatLyQuery } from '@/utils/vanHanhMenuPathUtils'
import { banHangVoucherTypeFromPath, buildBanHangPosEntryUrl } from '@/utils/banHangPosUrlUtils'
import { getAppVersionLabel } from '@/utils/appBuildInfo'
import PhieuTamTonKhoHeaderAlert from '@/components/layout/PhieuTamTonKhoHeaderAlert'

const HEADER_BREAKPOINT = 1200

const AppBuildVersionLine: React.FC<{ sx?: object }> = ({ sx }) => (
  <Typography
    variant="caption"
    title={getAppVersionLabel()}
    sx={{
      display: 'block',
      fontSize: 11,
      lineHeight: 1.35,
      color: banhangKiotLike.kiotTextMuted,
      userSelect: 'all',
      ...sx,
    }}
  >
    Version {getAppVersionLabel()}
  </Typography>
)

const HeaderMenu: React.FC = () => {
  const theme = useTheme()
  const { t } = useI18n()
  const { pathname, search } = useLocation()
  const { hasMenu, hasChucNangByCode, hasAnyChucNangByCode, hasAllChucNangByCode } =
    useMenuChucNang()
  const C = ALL_CHUCNANG_PERMISSION_CODES
  const currentKhoVatLyId = useCurrentKhoVatLyId()
  const menuTo = useCallback(
    (to: string) => withCurrentKhoVatLyQuery(to, currentKhoVatLyId),
    [currentKhoVatLyId],
  )

  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null)
  const [systemAnchor, setSystemAnchor] = useState<null | HTMLElement>(null)
  const [themeSubAnchor, setThemeSubAnchor] = useState<null | HTMLElement>(null)
  const [langSubAnchor, setLangSubAnchor] = useState<null | HTMLElement>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [useDrawerMenu, setUseDrawerMenu] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= HEADER_BREAKPOINT,
  )

  const onResize = useCallback(() => {
    const narrow = window.innerWidth <= HEADER_BREAKPOINT
    setUseDrawerMenu(narrow)
    if (!narrow) setDrawerOpen(false)
  }, [])

  useEffect(() => {
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [onResize])

  const visibleTopMenu = useMemo(() => {
    // C1: flag off → bỏ dropdown "Vận hành" khỏi drawer mobile (nguồn render riêng của HeaderMenu,
    // song song với visibleMenu của TopNavBar — giữ 2 chỗ cùng pattern filter)
    const base = enableVanHanh
      ? banhangTopMenu
      : banhangTopMenu.filter((entry) => entry.id !== 'vanHanhMenu')
    return filterBanhangTopMenuByPermissions(
      base,
      hasAnyChucNangByCode,
      hasAllChucNangByCode,
    )
  }, [hasAnyChucNangByCode, hasAllChucNangByCode])

  const systemMenuItems = useMemo(
    () =>
      filterBanhangNavDropdownItems(banhangSystemMenu.items, hasAnyChucNangByCode, hasAllChucNangByCode),
    [hasAnyChucNangByCode, hasAllChucNangByCode],
  )

  const drawerSections = useMemo(
    () =>
      buildBanhangMobileMenuSections(
        systemMenuItems.length > 0
          ? [...visibleTopMenu, { ...banhangSystemMenu, items: systemMenuItems }]
          : visibleTopMenu,
      ),
    [visibleTopMenu, systemMenuItems],
  )

  const showDashboard = hasMenu(routerConstants.dashboard) || hasChucNangByCode(C.WEB_BH_DASHBOARD_LIST)
  const showBanHang =
    hasMenu(routerConstants.banHang) || hasChucNangByCode(C.WEB_BH_HOA_DON_LIST)
  const showChat =
    hasMenu(routerConstants.chat) ||
    hasAnyChucNangByCode([C.WEB_CHAT_LIST, C.WEB_CHAT_CHAT])
  const banHangEntryUrl = buildBanHangPosEntryUrl(banHangVoucherTypeFromPath(pathname))
  const systemMenuActive = isBanhangDropdownActive(pathname, systemMenuItems, search)

  const handleUserOpen = (event: React.MouseEvent<HTMLElement>) => setUserAnchor(event.currentTarget)
  const handleUserClose = () => {
    setThemeSubAnchor(null)
    setLangSubAnchor(null)
    setUserAnchor(null)
  }
  const handleSystemClose = () => setSystemAnchor(null)

  const accountMenuItemSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1,
    py: 0.75,
    px: 1,
    mx: 0.5,
    minHeight: 36,
    borderRadius: `${banhangKiotLike.navItemRadiusPx}px`,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.25,
  } as const

  const accountMenuPaperSx = {
    minWidth: 220,
    maxWidth: 280,
    mt: 0.5,
    py: 0.5,
    borderRadius: `${banhangKiotLike.filterSidebarCardRadiusPx}px`,
    border: '1px solid rgba(0, 0, 0, 0.14)',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
    overflow: 'hidden',
    fontFamily: banhangKiotLike.kiotFontStack,
  } as const

  const accountSubMenuPaperSx = {
    ...accountMenuPaperSx,
    mt: 0,
    ml: -0.5,
    minWidth: 168,
    maxWidth: 220,
    py: 0.25,
  } as const

  const openThemeSubMenu = (el: HTMLElement) => {
    setLangSubAnchor(null)
    setThemeSubAnchor(el)
  }
  const openLangSubMenu = (el: HTMLElement) => {
    setThemeSubAnchor(null)
    setLangSubAnchor(el)
  }

  const handleLogout = () => {
    handleUserClose()
    logout()
  }

  const navR = banhangKiotLike.navItemRadiusPx

  const banHangNavHighlightColors = useMemo(
    () => ({
      activeBg: theme.palette.banhang.navItemActiveBg,
      activeBgHover: theme.palette.banhang.navItemActiveBgHover,
    }),
    [theme.palette.banhang.navItemActiveBg, theme.palette.banhang.navItemActiveBgHover],
  )

  const banHangButtonSx = useMemo(
    () => {
      const accent = theme.palette.primary.main
      const radius = `${banhangKiotLike.navItemActiveRadiusPx}px`
      return {
        ...buildKiotHeaderNavButtonSx(true, banHangNavHighlightColors),
        alignSelf: 'stretch' as const,
        height: '100%',
        minHeight: 0,
        bgcolor: '#fff',
        color: accent,
        border: '1px solid transparent',
        borderRadius: radius,
        '& .MuiButton-startIcon': { mr: '10px', ml: 0, color: 'inherit' },
        '&:hover': {
          bgcolor: '#f5f5f5',
          color: accent,
          border: '1px solid transparent',
          boxShadow: 'none',
          transform: 'none',
          borderRadius: radius,
        },
        '&:active': {
          bgcolor: '#eeeeee',
          color: accent,
          border: '1px solid transparent',
          boxShadow: 'none',
          transform: 'none',
        },
      }
    },
    [banHangNavHighlightColors, theme.palette.primary.main],
  )

  const headerIconButtonSx = useMemo(
    () => ({
      ...buildKiotHeaderIconButtonSx(banHangNavHighlightColors),
      alignSelf: 'stretch' as const,
      height: '100%',
      minHeight: 0,
    }),
    [banHangNavHighlightColors],
  )

  // Đọc lại khi mở menu — tránh tên/avatar stale sau khi cập nhật hồ sơ
  const accountProfile = useMemo(() => {
    const p = profileStorage.getProfile() as {
      username?: string
      hoTen?: string
      ten?: string
      fullName?: string
      avatar?: string | null
      firstName?: string
      lastName?: string
      userProfile?: {
        firstName?: string | null
        lastName?: string | null
        avatar?: string | null
      }
    } | null
    void userAnchor
    const up = p?.userProfile
    const first = String(up?.firstName ?? p?.firstName ?? '').trim()
    const last = String(up?.lastName ?? p?.lastName ?? '').trim()
    const fromProfile = [last, first].filter(Boolean).join(' ').trim()
    const displayName =
      (p?.hoTen && String(p.hoTen).trim()) ||
      (p?.ten && String(p.ten).trim()) ||
      (p?.fullName && String(p.fullName).trim()) ||
      fromProfile ||
      (p?.username && String(p.username).trim()) ||
      t('common.account')
    const username = p?.username ? String(p.username).trim() : ''
    const avatarRaw = String(up?.avatar ?? p?.avatar ?? '').trim()
    const avatarUrl = getImageDisplayUrl(avatarRaw) || undefined
    const letter = displayName.trim().charAt(0).toUpperCase() || '?'
    return { displayName, username, avatarUrl, letter }
  }, [t, userAnchor])

  const rightToolbarSx = {
    display: 'flex',
    alignItems: 'stretch',
    flexShrink: 0,
    height: '100%',
    boxSizing: 'border-box',
    gap: `${banhangKiotLike.kiotHeaderRightItemGapPx}px`,
    py: `${banhangKiotLike.kiotHeaderRightItemMarginYPx}px`,
  } as const

  const renderDrawerContent = () => (
    <Box sx={{ px: 0, py: 0.5 }}>
      <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 700, opacity: 0.8 }}>
        {t('components.menu.branchProject')}
      </Typography>
      <Box sx={{ px: 2, pb: 1 }}>
        <HeaderBranchSwitcher />
      </Box>
      <Divider sx={{ my: 1 }} />

      <List dense disablePadding>
        {showBanHang ? (
          <ListItemButton
            component={Link}
            to={banHangEntryUrl}
            target="_blank"
            rel="noopener noreferrer"
            selected
            onClick={() => setDrawerOpen(false)}
            sx={{
              py: 1.25,
              bgcolor: `${theme.palette.banhang.navItemActiveBg} !important`,
              color: '#fff',
              '&:hover': { bgcolor: `${theme.palette.banhang.navItemActiveBgHover} !important` },
            }}
          >
            <ShoppingCartOutlinedIcon sx={{ mr: 1.5, fontSize: 20 }} />
            <ListItemText
              primary={t('components.menu.banHang')}
              primaryTypographyProps={{ fontWeight: banhangKiotLike.kiotNavFontWeight }}
            />
          </ListItemButton>
        ) : null}

        {showDashboard ? (
          <ListItemButton
            component={Link}
            to={routerConstants.dashboard}
            selected={isBanhangPathActive(pathname, routerConstants.dashboard)}
            onClick={() => setDrawerOpen(false)}
            sx={{ py: 1.25 }}
          >
            <HomeOutlinedIcon sx={{ mr: 1.5, fontSize: 20, opacity: 0.85 }} />
            <ListItemText
              primary={t('components.menu.home')}
              primaryTypographyProps={{ fontWeight: banhangKiotLike.kiotNavFontWeight }}
            />
          </ListItemButton>
        ) : null}

        {showChat ? (
          <ListItemButton
            component={Link}
            to={routerConstants.chat}
            selected={isBanhangPathActive(pathname, routerConstants.chat)}
            onClick={() => setDrawerOpen(false)}
            sx={{ py: 1.25 }}
          >
            <ChatBubbleOutlineIcon sx={{ mr: 1.5, fontSize: 20, opacity: 0.85 }} />
            <ListItemText
              primary="Tin nhắn"
              primaryTypographyProps={{ fontWeight: banhangKiotLike.kiotNavFontWeight }}
            />
          </ListItemButton>
        ) : null}

        {drawerSections.map((section, si) => (
          <React.Fragment key={si}>
            {section.subheaderKey ? (
              <ListSubheader sx={{ lineHeight: '36px', fontWeight: 700, fontSize: '0.75rem' }}>
                {t(section.subheaderKey)}
              </ListSubheader>
            ) : null}
            {section.items.map((item, ii) => {
              const active = isBanhangPathActive(pathname, item.to, search)
              const prevGroup = ii > 0 ? section.items[ii - 1].groupKey : undefined
              const showGroup = item.groupKey && item.groupKey !== prevGroup
              return (
                <React.Fragment key={`${si}-${item.to}`}>
                  {showGroup ? (
                    <ListSubheader sx={{ lineHeight: '32px', fontWeight: 700, fontSize: '0.6875rem', pl: 2 }}>
                      {t(item.groupKey!)}
                    </ListSubheader>
                  ) : null}
                  <ListItemButton
                    component={Link}
                    to={menuTo(item.to)}
                    onClick={() => setDrawerOpen(false)}
                    selected={active}
                    sx={{ py: 1.25, pl: item.groupKey ? 3 : 2 }}
                  >
                    <ListItemText
                      primary={t(item.labelKey)}
                      primaryTypographyProps={{
                        fontWeight: banhangKiotLike.kiotNavFontWeight,
                        fontSize: '0.9375rem',
                      }}
                    />
                  </ListItemButton>
                </React.Fragment>
              )
            })}
          </React.Fragment>
        ))}
      </List>

      <Divider sx={{ my: 1 }} />

      <List dense disablePadding>
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar
            src={accountProfile.avatarUrl}
            sx={{ width: 36, height: 36, fontSize: 15, fontWeight: 700, bgcolor: 'primary.main' }}
          >
            {accountProfile.letter}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: banhangKiotLike.kiotTextPrimary,
                lineHeight: 1.3,
                wordBreak: 'break-word',
              }}
            >
              {accountProfile.displayName}
            </Typography>
            {accountProfile.username ? (
              <Typography sx={{ fontSize: 12, color: banhangKiotLike.kiotTextMuted }} noWrap>
                {accountProfile.username}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <ListItemButton component={Link} to={routerConstants.account} onClick={() => setDrawerOpen(false)} sx={{ py: 1.25 }}>
          <PersonOutline sx={{ mr: 1.5, fontSize: 20, opacity: 0.85 }} />
          <ListItemText
            primary={t('common.account')}
            primaryTypographyProps={{ fontWeight: banhangKiotLike.kiotNavFontWeight }}
          />
        </ListItemButton>
        <ListItemButton
          onClick={() => {
            setDrawerOpen(false)
            logout()
          }}
          sx={{ py: 1.25 }}
        >
          <LogoutOutlinedIcon sx={{ mr: 1.5, fontSize: 20, opacity: 0.85 }} />
          <ListItemText
            primary={t('auth.logout')}
            primaryTypographyProps={{ fontWeight: banhangKiotLike.kiotNavFontWeight }}
          />
        </ListItemButton>
        <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
          <AppBuildVersionLine />
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Typography
          variant="caption"
          sx={{
            px: 2,
            pt: 1,
            pb: 0.5,
            display: 'block',
            fontWeight: 700,
            color: banhangKiotLike.kiotTextMuted,
            fontFamily: banhangKiotLike.kiotFontStack,
          }}
        >
          {t('components.menu.system')}
        </Typography>
        {HE_THONG_PROD_LINKS.map((item) => (
          <ListItemButton
            key={item.id}
            component="a"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setDrawerOpen(false)}
            sx={{ py: 1.25 }}
          >
            <OpenInNewOutlinedIcon sx={{ mr: 1.5, fontSize: 18, opacity: 0.85 }} />
            <ListItemText
              primary={t(item.labelKey)}
              primaryTypographyProps={{ fontWeight: banhangKiotLike.kiotNavFontWeight }}
            />
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, opacity: 0.8 }}>
          {t('resources.users.fields.theme')}
        </Typography>
        <ThemeAccentPicker variant="menu" onPicked={() => setDrawerOpen(false)} />
      </Box>
      <Box sx={{ px: 2, pt: 1, pb: 2 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, opacity: 0.8 }}>
          {t('resources.users.fields.language')}
        </Typography>
        <LanguageSwitcher />
      </Box>
    </Box>
  )

  const toolbarInner = (
    <>
      {useDrawerMenu ? (
        <IconButton
          color="inherit"
          edge="start"
          size="small"
          onClick={() => setDrawerOpen(true)}
          aria-label="Mở menu"
          sx={{
            color: '#fff',
            alignSelf: 'stretch',
            height: '100%',
            minWidth: 40,
            borderRadius: `${navR}px`,
            py: 0,
            mr: 0,
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      ) : null}

      {!useDrawerMenu ? <HeaderBranchSwitcher /> : null}

      {!useDrawerMenu ? (
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'stretch', height: '100%' }}>
          <TopNavBar />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }} />
      )}

      {useDrawerMenu ? <HeaderBranchSwitcher /> : null}

      <Box sx={rightToolbarSx}>
        {showBanHang ? (
          <Button
            component={Link}
            to={banHangEntryUrl}
            target="_blank"
            rel="noopener noreferrer"
            color="inherit"
            size="small"
            disableElevation
            disableRipple
            startIcon={<ShoppingCartOutlinedIcon sx={{ fontSize: banhangKiotLike.kiotNavIconSizePx }} />}
            sx={banHangButtonSx}
          >
            {t('components.menu.banHang')}
          </Button>
        ) : null}
        {showChat ? (
          <Tooltip title="Tin nhắn">
            <IconButton
              component={Link}
              to={routerConstants.chat}
              size="small"
              color="inherit"
              aria-label="Tin nhắn"
              sx={{
                ...headerIconButtonSx,
                ...(isBanhangPathActive(pathname, routerConstants.chat)
                  ? { bgcolor: banHangNavHighlightColors.activeBg }
                  : null),
              }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        ) : null}
        <PhieuTamTonKhoHeaderAlert />

        {systemMenuItems.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%', minHeight: 0 }}>
            <IconButton
              size="small"
              color="inherit"
              aria-label={t('components.menu.system')}
              aria-haspopup="true"
              aria-expanded={Boolean(systemAnchor)}
              onClick={(e) => setSystemAnchor(e.currentTarget)}
              sx={{
                ...headerIconButtonSx,
                ...(systemMenuActive || systemAnchor
                  ? { bgcolor: banHangNavHighlightColors.activeBg }
                  : null),
              }}
            >
              <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Menu
              anchorEl={systemAnchor}
              open={Boolean(systemAnchor)}
              onClose={handleSystemClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 220,
                    mt: 0.5,
                    borderRadius: `${banhangKiotLike.filterSidebarCardRadiusPx}px`,
                    border: '1px solid rgba(0, 0, 0, 0.14)',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
                  },
                },
              }}
            >
              {systemMenuItems.map((item) => (
                <MenuItem
                  key={item.id}
                  component={Link}
                  to={item.to}
                  selected={isBanhangPathActive(pathname, item.to, search)}
                  onClick={handleSystemClose}
                  sx={{ gap: 1 }}
                >
                  {item.icon ? (
                    <Box component="span" sx={{ display: 'inline-flex', opacity: 0.85 }}>
                      {item.icon}
                    </Box>
                  ) : null}
                  {t(item.labelKey)}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%', minHeight: 0 }}>
          <IconButton
            size="small"
            color="inherit"
            aria-label={accountProfile.displayName}
            aria-haspopup="true"
            aria-expanded={Boolean(userAnchor)}
            onClick={handleUserOpen}
            sx={{
              ...headerIconButtonSx,
              p: 0,
              overflow: 'hidden',
            }}
          >
            <Avatar
              src={accountProfile.avatarUrl}
              alt={accountProfile.displayName}
              sx={{
                width: '100%',
                height: '100%',
                fontSize: 15,
                fontWeight: 700,
                borderRadius: 'inherit',
                bgcolor: accountProfile.avatarUrl ? 'transparent' : 'rgba(255,255,255,0.22)',
                color: '#fff',
              }}
            >
              {accountProfile.letter}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={userAnchor}
            open={Boolean(userAnchor)}
            onClose={handleUserClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: { sx: accountMenuPaperSx },
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.25,
                bgcolor: '#f7f8fa',
                borderBottom: '1px solid',
                borderColor: banhangKiotLike.filterSectionDividerColor,
              }}
            >
              <Avatar
                src={accountProfile.avatarUrl}
                alt={accountProfile.displayName}
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  fontSize: 16,
                  fontWeight: 700,
                  bgcolor: 'primary.main',
                  color: '#fff',
                }}
              >
                {accountProfile.letter}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.15 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: banhangKiotLike.kiotTextPrimary,
                    lineHeight: 1.3,
                    wordBreak: 'break-word',
                  }}
                >
                  {accountProfile.displayName}
                </Typography>
                {accountProfile.username ? (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: banhangKiotLike.kiotTextMuted,
                      lineHeight: 1.3,
                    }}
                    noWrap
                  >
                    {accountProfile.username}
                  </Typography>
                ) : null}
              </Box>
            </Box>

            <MenuItem
              component={Link}
              to={routerConstants.account}
              onClick={handleUserClose}
              sx={{ ...accountMenuItemSx, mt: 0.25 }}
            >
              <PersonOutline fontSize="small" sx={{ opacity: 0.85 }} />
              <Box component="span" sx={{ flex: 1 }}>
                {t('common.account')}
              </Box>
            </MenuItem>

            <MenuItem
              onClick={(e) => {
                e.stopPropagation()
                openThemeSubMenu(e.currentTarget)
              }}
              selected={Boolean(themeSubAnchor)}
              sx={accountMenuItemSx}
            >
              <ChevronLeftIcon sx={{ fontSize: 16, opacity: 0.5, flexShrink: 0 }} />
              <PaletteOutlinedIcon fontSize="small" sx={{ opacity: 0.85 }} />
              <Box component="span" sx={{ flex: 1 }}>
                {t('resources.users.fields.theme')}
              </Box>
            </MenuItem>
            <Menu
              anchorEl={themeSubAnchor}
              open={Boolean(themeSubAnchor)}
              onClose={() => setThemeSubAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              disableAutoFocusItem
              disableEnforceFocus
              disableRestoreFocus
              hideBackdrop
              sx={{ pointerEvents: 'none' }}
              slotProps={{
                paper: { sx: { ...accountSubMenuPaperSx, pointerEvents: 'auto' } },
              }}
            >
              <Box sx={{ px: 1.25, py: 1 }}>
                <ThemeAccentPicker variant="menu" onPicked={handleUserClose} />
              </Box>
            </Menu>

            <MenuItem
              onClick={(e) => {
                e.stopPropagation()
                openLangSubMenu(e.currentTarget)
              }}
              selected={Boolean(langSubAnchor)}
              sx={accountMenuItemSx}
            >
              <ChevronLeftIcon sx={{ fontSize: 16, opacity: 0.5, flexShrink: 0 }} />
              <TranslateOutlinedIcon fontSize="small" sx={{ opacity: 0.85 }} />
              <Box component="span" sx={{ flex: 1 }}>
                {t('resources.users.fields.language')}
              </Box>
            </MenuItem>
            <Menu
              anchorEl={langSubAnchor}
              open={Boolean(langSubAnchor)}
              onClose={() => setLangSubAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              disableAutoFocusItem
              disableEnforceFocus
              disableRestoreFocus
              hideBackdrop
              sx={{ pointerEvents: 'none' }}
              slotProps={{
                paper: { sx: { ...accountSubMenuPaperSx, pointerEvents: 'auto' } },
              }}
            >
              <LanguageSwitcher menuMode onPicked={handleUserClose} />
            </Menu>

            <Divider sx={{ my: 0.5, borderColor: banhangKiotLike.filterSectionDividerColor }} />

            {HE_THONG_PROD_LINKS.map((item) => (
              <MenuItem
                key={item.id}
                component="a"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleUserClose}
                sx={{ ...accountMenuItemSx, fontWeight: 500 }}
              >
                <OpenInNewOutlinedIcon fontSize="small" sx={{ opacity: 0.7 }} />
                <Box component="span" sx={{ flex: 1 }}>
                  {t(item.labelKey)}
                </Box>
              </MenuItem>
            ))}

            <Divider sx={{ my: 0.5, borderColor: banhangKiotLike.filterSectionDividerColor }} />

            <MenuItem
              onClick={handleLogout}
              sx={{
                ...accountMenuItemSx,
                mb: 0.25,
                color: '#c62828',
                '&:hover': { bgcolor: 'rgba(198, 40, 40, 0.06)' },
              }}
            >
              <LogoutOutlinedIcon fontSize="small" sx={{ opacity: 0.9 }} />
              <Box component="span" sx={{ flex: 1 }}>
                {t('auth.logout')}
              </Box>
            </MenuItem>
            <Box sx={{ px: 1.5, pt: 0.25, pb: 1 }}>
              <AppBuildVersionLine />
            </Box>
          </Menu>
        </Box>

        {/* C1: chuông thông báo vận hành — ẩn khi flag off (kể cả user có WEB_BH_THONG_BAO) */}
        {enableVanHanh ? <InAppNotificationMenu headerMode /> : null}
      </Box>
    </>
  )

  return (
    <>
      <AppBar
        position="static"
        color="primary"
        elevation={0}
        sx={{
          position: 'relative',
          zIndex: (z) => z.zIndex.drawer + 10,
          bgcolor: theme.palette.banhang.headerBg,
          color: banhangKiotLike.topNavText,
          borderBottom: 'none',
          boxShadow: 'none',
          '& .MuiToolbar-root': {
            alignItems: 'stretch',
            minHeight: LAYOUT_APP_BAR_HEIGHT_PX,
            maxHeight: LAYOUT_APP_BAR_HEIGHT_PX,
          },
        }}
      >
        <Toolbar
          variant="dense"
          disableGutters
          sx={{
            width: '100%',
            minHeight: TOP_NAV_HEIGHT_PX,
            maxHeight: TOP_NAV_HEIGHT_PX,
            height: TOP_NAV_HEIGHT_PX,
            py: 0,
            px: `${banhangKiotLike.kiotShellHeaderPaddingXPx}px`,
            m: 0,
            gap: 0,
            boxSizing: 'border-box',
          }}
        >
          {toolbarInner}
        </Toolbar>
      </AppBar>

      {useDrawerMenu ? (
        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 320 } }}>
          <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle1" sx={{ px: 2, py: 1.5, fontWeight: 700 }}>
              Menu
            </Typography>
            {renderDrawerContent()}
          </Box>
        </Drawer>
      ) : null}
    </>
  )
}

export default HeaderMenu
